// Vuex module for onboarding todo list
import accountService from '@/services/account/accountService';
import analyticsService from '@/services/analytics/analyticsService';
import SkillsService from '@/services/skills/SkillsService';

const defaultTodos = [
    {
        id: 1,
        titleKey: 'gettingStarted.onboardingTodos.installWebsitePlatform.title',
        descriptionKey: 'gettingStarted.onboardingTodos.installWebsitePlatform.description',
        completed: false,
        image: 'https://firebasestorage.googleapis.com/v0/b/lixsa-prod.appspot.com/o/public_images%2Fonboarding%2FPaso%201.jpg?alt=media&token=bfb043ce-47ea-4eb2-b5f6-247442ae01b1',
    },
    {
        id: 2,
        titleKey: 'gettingStarted.onboardingTodos.tryLixsa.title',
        descriptionKey: 'gettingStarted.onboardingTodos.tryLixsa.description',
        completed: false,
        image: 'https://firebasestorage.googleapis.com/v0/b/lixsa-prod.appspot.com/o/public_images%2Fonboarding%2FPaso%202.jpg?alt=media&token=6d560a4d-a9b2-4c56-83c1-400df52ed17b',
    },
    {
        id: 3,
        titleKey: 'gettingStarted.onboardingTodos.installCommunicationChannel.title',
        descriptionKey: 'gettingStarted.onboardingTodos.installCommunicationChannel.description',
        completed: false,
        image: 'https://firebasestorage.googleapis.com/v0/b/lixsa-prod.appspot.com/o/public_images%2Fonboarding%2FPaso%203.jpg?alt=media&token=994b7c45-7eed-4f38-a22e-24c986e29e40',
    },
    {
        id: 4,
        titleKey: 'gettingStarted.onboardingTodos.reviewTrainingResources.title',
        descriptionKey: 'gettingStarted.onboardingTodos.reviewTrainingResources.description',
        completed: false,
        image: 'https://firebasestorage.googleapis.com/v0/b/lixsa-prod.appspot.com/o/public_images%2Fonboarding%2FPaso%204.jpg?alt=media&token=0c7ea841-e3d6-4829-af1a-cde99913f311',
    },
];

const state = () => ({
    todos: JSON.parse(localStorage.getItem('onboardingTodos')) || defaultTodos,
    isPanelOpen: false,
});

const getters = {
    allTodos: (state, getters, rootState, rootGetters) => {
        const selectedPlatform = rootGetters['user/getSelectedPlatformFromMetadata'];
        const selectedChannel = rootGetters['user/getSelectedChannelFromMetadata'];

        return state.todos.map((todo) => {
            let titleKey = todo.titleKey;
            let descriptionKey = todo.descriptionKey;

            if (
                todo.id === 1 &&
                selectedPlatform &&
                selectedPlatform.toLowerCase() !== 'none' &&
                selectedPlatform.toLowerCase() !== 'other'
            ) {
                titleKey = `Install ${selectedPlatform}`;
                descriptionKey = `Install your ${selectedPlatform} platform and connect it to Lixsa.`;
            }
            if (
                todo.id === 3 &&
                selectedChannel &&
                selectedChannel.toLowerCase() !== 'none' &&
                selectedChannel.toLowerCase() !== 'other'
            ) {
                titleKey = `Install ${selectedChannel}`;
                descriptionKey = `Install ${selectedChannel} to enable Lixsa to communicate with your customers.`;
            }

            return {
                ...todo,
                titleKey,
                descriptionKey,
            };
        });
    },
    completedTodos: (state, getters) => getters.allTodos.filter((todo) => todo.completed),
    incompleteTodos: (state, getters) => getters.allTodos.filter((todo) => !todo.completed),
    progress: (state, getters) => {
        const todos = getters.allTodos;
        const total = todos.length;
        const done = todos.filter((t) => t.completed).length;
        return total === 0 ? 0 : Math.round((done / total) * 100);
    },
    isAllComplete: (state, getters) => getters.allTodos.every((todo) => todo.completed),
    isPanelOpen: (state) => state.isPanelOpen,
    getActionButton: () => (todo) => {
        // Define action buttons based on todo ID
        const actionButtons = {
            1: 'Install Platform',
            2: 'Try Lixsa',
            3: 'Install Channel',
            4: 'Review Resources',
        };
        return actionButtons[todo.id] || null;
    },
    getTodoStatus: (state) => (id) => {
        const todo = state.todos.find((t) => t.id === id);
        return todo ? todo.completed : false;
    },
};

const mutations = {
    MARK_TODO_COMPLETE(state, id) {
        const todo = state.todos.find((t) => t.id === id);
        if (todo) todo.completed = true;
        localStorage.setItem('onboardingTodos', JSON.stringify(state.todos));
    },
    MARK_TODO_INCOMPLETE(state, id) {
        const todo = state.todos.find((t) => t.id === id);
        if (todo) todo.completed = false;
        localStorage.setItem('onboardingTodos', JSON.stringify(state.todos));
    },
    RESET_TODOS(state) {
        state.todos = defaultTodos.map((t) => ({ ...t }));
        localStorage.setItem('onboardingTodos', JSON.stringify(state.todos));
    },
    SET_TODOS(state, todos) {
        state.todos = todos;
        localStorage.setItem('onboardingTodos', JSON.stringify(state.todos));
    },
    OPEN_PANEL(state) {
        state.isPanelOpen = true;
    },
    CLOSE_PANEL(state) {
        state.isPanelOpen = false;
    },
    TOGGLE_PANEL(state) {
        state.isPanelOpen = !state.isPanelOpen;
    },
};

const actions = {
    markTodoComplete({ commit, state }, id) {
        const todo = state.todos.find((t) => t.id === id);
        analyticsService.trackEvent('checklist_step_completed', {
            todo_id: id,
            todo_title: todo.titleKey,
            todo_description: todo.descriptionKey,
        });
        commit('MARK_TODO_COMPLETE', id);
    },
    markTodoIncomplete({ commit }, id) {
        commit('MARK_TODO_INCOMPLETE', id);
    },
    resetTodos({ commit }) {
        commit('RESET_TODOS');
    },
    setTodos({ commit }, todos) {
        commit('SET_TODOS', todos);
    },
    openPanel({ commit }) {
        commit('OPEN_PANEL');
    },

    openPanelWithDelay({ dispatch }) {
        setTimeout(() => {
            dispatch('openPanel');
        }, 2000);
    },

    closePanel({ commit }) {
        commit('CLOSE_PANEL');
    },
    togglePanel({ commit }) {
        commit('TOGGLE_PANEL');
    },

    async saveTodoStateInMetadata({ rootGetters, state, dispatch }) {
        const metadata = rootGetters['user/getMetadata'];
        if (!metadata) return;

        const todos = state.todos;
        metadata.onboardingTodos = todos;
        try {
            await accountService.saveMetadata(metadata);
            await dispatch('user/actionGetAccount');
        } catch (error) {
            console.error('Error saving todo state in metadata', error);
        }
    },

    async fetchTodoStateFromMetadata({ rootGetters, commit }) {
        const metadata = rootGetters['user/getMetadata'];
        const todos = metadata?.onboardingTodos;
        if (todos) {
            commit('SET_TODOS', todos);
        }
    },

    async initializeTodos({ commit, rootGetters, dispatch }) {
        await dispatch('fetchTodoStateFromMetadata');

        const hasAnyMainApp = rootGetters['agentSettings/hasAnyMainApp'];
        const hasAnyCommunicationChannel = rootGetters['agentSettings/hasAnyCommunicationChannel'];

        if (hasAnyMainApp && hasAnyCommunicationChannel) {
            commit('MARK_TODO_COMPLETE', 1);
            commit('MARK_TODO_COMPLETE', 2);
            commit('MARK_TODO_COMPLETE', 3);
            commit('MARK_TODO_COMPLETE', 4);
            return;
        }

        if (hasAnyMainApp) {
            commit('MARK_TODO_COMPLETE', 1);
            return;
        }
    },

    handleStepCompletion({ dispatch }, { stepId }) {
        dispatch('markTodoComplete', stepId);
        dispatch('saveTodoStateInMetadata');
    },

    // Step action handlers
    async handleStepAction({ dispatch }, { stepId, router }) {
        // Navigate based on the step
        switch (stepId) {
            case 1:
                await dispatch('goToPlatformInstallation', router);
                break;
            case 2:
                router.push('/agent/playground');
                dispatch('handleStepCompletion', { stepId: 2 });
                break;
            case 3:
                await dispatch('goToChannelInstallation', router);
                break;
            case 4:
                router.push('/agent-studio/training');
                dispatch('handleStepCompletion', { stepId: 4 });
                break;
        }

        dispatch('openPanelWithDelay');
    },

    async goToPlatformInstallation({ rootGetters }, router) {
        try {
            const response = await SkillsService.searchSkills(null, 'main app');
            const selectedPlatform = rootGetters['user/getSelectedPlatformFromMetadata'];
            const skill = response?.data?.content.find((skill) => skill.name === selectedPlatform);
            if (skill) {
                router.push(`/integrations/details/${skill.uuid}?autostart=true`);
            } else {
                router.push('/integrations/discovery?type=main%20app');
            }
        } catch (error) {
            console.error(error);
        }
    },

    async goToChannelInstallation({ rootGetters }, router) {
        try {
            const response = await SkillsService.searchSkills(null, 'channel');
            const selectedChannel = rootGetters['user/getSelectedChannelFromMetadata'];
            const skill = response?.data?.content.find((skill) => skill.name === selectedChannel);
            if (skill) {
                router.push(`/integrations/details/${skill.uuid}?autostart=true`);
            } else {
                router.push('/integrations/discovery?type=channel');
            }
        } catch (error) {
            console.error(error);
        }
    },

    clearState({ commit }) {
        commit('SET_TODOS', defaultTodos);
        commit('SET_IS_PANEL_OPEN', false);
        localStorage.removeItem('onboardingTodos');
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
